import Component from '@bythepixel/component-loader';

export default class HeaderNav extends Component {
  static loaderPriority = 'in-view';
  static selector = '[data-comp-header-nav]';

  #state = {
    visible: false,
    scrolledPastHeaderNav: false,
  }

  constructor(containerEl) {
    super(containerEl);
    this.navToggle = this.$container.querySelector('[data-mobile-nav-toggle]');
    this.navTarget = this.$container.querySelector('[data-mobile-nav-target]');
    this.navLinksMenu = this.$container.querySelector('[data-nav-links-menu]');
    this.headerNavbar = this.$container.querySelector('[data-header-navbar]');
    this.headerNavContainer = this.$container.querySelector('[data-header-nav-container]');
    this.headerNavLogoContainer = this.$container.querySelector('[data-header-nav-logo-container]');
    this.headerNavLogoContainerOriginallyHidden = this.headerNavLogoContainer.classList.contains('hidden');
    this.headerNavActiveClass = 'header-nav--active';
    this.lightNavLogo = this.$container.querySelector('[data-header-nav-logo-light]');
    this.darkNavLogo = this.$container.querySelector('[data-header-nav-logo-dark]');
    this.dropdowns = this.$container.querySelectorAll('[data-header-nav-dropdown]');
    this.dropdownNavs = this.$container.querySelectorAll('[data-header-nav-dropdown-menu]');
    this.dropdownTriggers = this.$container.querySelectorAll('[data-header-nav-dropdown-trigger]');
    this.isProductDetailPage = this.$container.dataset.compIsProductDetailsPage === 'true';
    this.init();
  }

  init() {
    this.addNavToggleListener();
    this.addWindowResizeListener();
    this.addActiveLinkClass();

    if (!this.isProductDetailPage) {
      this.addScrollUpListener();
    }

    this.determineNavMenuHeight();
    this.dropdownNavEvents();
  }

  addNavToggleListener() {
    this.navToggle.addEventListener('click', () => {
      this.headerNavContainer.classList.toggle(this.headerNavActiveClass);
      this.headerNavbar.classList.toggle('bg-white');
      this.toggleState();
      this.toggleBodyStyle();
      this.determineNavLogoColor();
      this.determineNavMenuHeight();
      if (this.headerNavLogoContainerOriginallyHidden) {
        this.determineNavLogoDisplayState();
      }
    });
  }

  addWindowResizeListener() {
    window.addEventListener('resize', () => {
      this.determineNavLogoColor();
      this.determineNavMenuHeight();
    });
  }

  dropdownNavEvents() {
    if (this.dropdownTriggers.length === 0 || this.dropdownNavs.length === 0) {
      return;
    }

    this.dropdownTriggers.forEach((trigger, index) => {
      trigger.addEventListener('click', () => {
        this.dropdowns[index].classList.toggle('header-nav-dropdown--active');
        this.dropdownNavs[index].style.height = `${this.getNavHeightOffset() - (trigger.offsetHeight * 2)}px`;

        if (this.dropdowns[index].classList.contains('header-nav-dropdown--active')) {
          this.navTarget.style.overflow = 'hidden';
        } else {
          this.navTarget.style.overflow = 'scroll';
        }
      });
    });
  }

  getNavHeightOffset() {
    const parentTop = this.$container.parentElement.offsetTop;
    const windowHeight = window.innerHeight;

    return windowHeight - parentTop;
  }

  toggleState() {
    if (this.#state.visible === false) {
      this.#state.visible = true;
    } else {
      this.#state.visible = false;
    }
  }

  toggleBodyStyle() {
    if (this.#state.visible === true) {
      setFixedBodyStyle();
    } else {
      removeFixedBodyStyle();
    }
  }

  determineNavLogoColor() {
    if (window.innerWidth < 768) {
      if (this.$container.classList.contains('header-nav--mobile-light')) {
        if (this.#state.visible === true) {
          this.displayDarkNavLogo();
        } else if (this.$container.classList.contains('header-nav--fixed')) {
          this.displayDarkNavLogo();
        } else {
          this.displayLightNavLogo();
        }
      } else {
        this.displayDarkNavLogo();
      }
    } else if (this.$container.classList.contains('header-nav--desktop-light')) {
      this.displayLightNavLogo();
    } else {
      this.displayDarkNavLogo();
    }
  }

  displayLightNavLogo() {
    this.lightNavLogo.classList.add('inline', 'md:inline');
    this.lightNavLogo.classList.remove('hidden', 'md:hidden');
    this.darkNavLogo.classList.add('hidden', 'md:hidden');
    this.darkNavLogo.classList.remove('inline', 'md:inline');
  }

  displayDarkNavLogo() {
    this.darkNavLogo.classList.add('inline', 'md:inline');
    this.darkNavLogo.classList.remove('hidden', 'md:hidden');
    this.lightNavLogo.classList.add('hidden', 'md:hidden');
    this.lightNavLogo.classList.remove('inline', 'md:inline');
  }

  determineNavLogoDisplayState() {
    if (this.#state.visible === true) {
      this.headerNavLogoContainer.classList.remove('hidden');
    } else if (this.$container.classList.contains('header-nav--fixed')) {
      this.headerNavLogoContainer.classList.remove('hidden');
    } else {
      this.headerNavLogoContainer.classList.add('hidden');
    }
  }

  determineNavMenuHeight() {
    if (window.innerWidth > 768) {
      this.navTarget.style.height = 'auto';
    } else {
      this.navTarget.style.height = `${this.getNavHeightOffset()}px`;
    }
  }

  addActiveLinkClass() {
    const windowPath = window.location;
    const activeLinkEl = this.navLinksMenu.querySelector(`a[href="${windowPath}"]`);
    if (activeLinkEl) {
      activeLinkEl.classList.add('active-link');
    }
  }

  addScrollUpListener() {
    let lastScroll = 0;
    window.addEventListener('scroll', () => {
      requestAnimationFrame(() => {
        const currentScroll = document.documentElement.scrollTop;
        const parentTop = this.$container.parentElement.offsetTop;
        const headerNavBottom = parentTop + this.$container.offsetHeight;
        if (currentScroll <= parentTop) {
          if (this.#state.scrolledPastHeaderNav === true) {
            this.unfixHeaderNav();
            lastScroll = 0;
          }
        } else if (currentScroll > headerNavBottom) {
          if (lastScroll < currentScroll) {
            if (!this.$container.classList.contains('-translate-y-full')) {
              this.$container.classList.add('transform', '-translate-y-full');
            }
          } else {
            if (this.#state.scrolledPastHeaderNav === false) {
              this.fixHeaderNav();
            }
            if (this.$container.classList.contains('-translate-y-full')) {
              this.$container.classList.remove('transform', '-translate-y-full');
            }
          }
          lastScroll = currentScroll;
        }
      });
    });
  }

  fixHeaderNav() {
    this.#state.scrolledPastHeaderNav = true;
    this.$container.classList.add('header-nav--fixed', 'transform', '-translate-y-full', 'transition-transform');
    if (this.headerNavLogoContainer.classList.contains('hidden')) {
      this.headerNavLogoContainer.classList.remove('hidden');
    }
    this.displayDarkNavLogo();
  }

  unfixHeaderNav() {
    this.#state.scrolledPastHeaderNav = false;
    this.$container.classList.remove('transition-transform', 'header-nav--fixed');
    this.determineNavLogoColor();
    if (this.headerNavLogoContainerOriginallyHidden) {
      this.headerNavLogoContainer.classList.add('hidden');
    }
  }
}

function setFixedBodyStyle() {
  Object.assign(document.body.style, {
    overflow: 'hidden',
    height: '100%',
    width: '100%',
  });
}

function removeFixedBodyStyle() {
  Object.assign(document.body.style, {
    overflow: null,
    height: null,
    width: null,
  });
}
